import { LitElement, html, css, PropertyValues, TemplateResult } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { FiSubView, UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { VoyageShoreContextModel } from '../../../src/models/voyage-shore-context-model';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { TimeseriesData } from '../../voy-timeseries-chart/src/echartUtils';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { YAxisChartParams } from '../../voy-timeseries-chart/src/echarts-options';
import { VoyageCheckbox } from '../../voyage-checkbox/src/voyage-checkbox';
import { PastTrailData, VoyagePastTrailModel, VoyagePastTrailModelEvents } from '../../../src/models/voyage-past-trail-model';
import { formatPastTrailValue, positionCols, tableTitles, weatherCols } from './voy-past-trail-table';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { VesselInfo } from '../../../src/models/types/vessels-model.types';
import { OperatorId, VesselVoyageVersion } from '../../voy-global-filters/src/types';
import { QuickDates, DateType } from './types';
import { VesselsModel } from '../../../src/models/vessels-model';
import { ShoreContextEvent } from '../../../src/models/types/voyage-shore-context-model.types';
import '../../voyage-checkbox/src/voyage-checkbox';
import "../../voy-dropdown/src/voy-dropdown";
import "./voy-past-trail-table";
import '../../voyage-spinner/src/voyage-spinner';
import "@maersk-global/mds-components-core/mc-button";

const dropdownData = [
    QuickDates.CurrentVoyage,
    QuickDates.Hours_12,
    QuickDates.Day_1,
    QuickDates.Day_3,
    QuickDates.Month_1
];

@customElement('voy-past-trail-view')
export class VoyPastTrailView extends LitElement {
    @property({ type: Number }) public imo?: number;
    @property({ attribute: false }) private _vessel?: VesselInfo;
    @property({ attribute: false }) private _datepickerMaxValue: string = this._formatDateForDatepicker(new Date());
    @property({ attribute: false }) private _datepickerMinValue: string = this._formatDateForDatepicker(new Date(new Date().getMonth() - 1));
    @property({ attribute: false }) private _fromDate: string = "";
    @property({ attribute: false }) private _toDate: string = this._formatDateForDatepicker(new Date());
    @property({ attribute: false }) private _initialSelectedItem: string|null = "Current Voyage";
    @property({ attribute: false }) private _chartData: TimeseriesData[] = [];
    @property({ attribute: false }) private _pastTrailData: PastTrailData[] | null = null;
    @property({ attribute: false }) private _maximized: boolean = false;
    @property({ attribute: false }) private _dataTableExpanded: boolean = false;
    @property({ attribute: false }) private _chartStyling?: YAxisChartParams;
    @property({ attribute: false }) private _customDisplayText: string | null = null;
    @property({ attribute: false }) private _showLoadingSpinner: boolean = false;
    @property({ attribute: false }) private _showWeather: boolean = false;
    @property({ attribute: false }) private _dataIsFetching: boolean = false;
    @property({ attribute: false }) private _weatherDataIsFetching: boolean = false;
    @property({ attribute: false }) private _pastTrailDataReversed: PastTrailData[]|null = null;
    @property({ attribute: false }) private _selectedQuickDate: QuickDates = QuickDates.CurrentVoyage;
    @property({ attribute: false }) private _showContainer: boolean = true;
    @property({ type:Boolean }) private  _timeLineVisible : any =  false

    private _datepickerMaxValueInterval: any;
    private _spinnerTimeout:any;

    constructor() {
        super()
        this._setVessel = this._setVessel.bind(this);
        this._goToSubView = this._goToSubView.bind(this);
        this._applyQuickDate = this._applyQuickDate.bind(this);
        this._getFromDateFromSchedule = this._getFromDateFromSchedule.bind(this);
        this._dateHandler = this._dateHandler.bind(this);
        this._updateChart = this._updateChart.bind(this);
        this._updatePerformanceData = this._updatePerformanceData.bind(this);
        this._updateNowValue = this._updateNowValue.bind(this);
        this._renderChartContent = this._renderChartContent.bind(this);
        this._dataFetched = this._dataFetched.bind(this);
        this._fetchWeather = this._fetchWeather.bind(this);
        this._exportCSV = this._exportCSV.bind(this);
        this._getFromDateForNonMaerskVessel = this._getFromDateForNonMaerskVessel.bind(this);

        this._datepickerMaxValueInterval = setInterval(this._updateNowValue, 1000*60*30); // update toDate (and the chart) every 30 minutes
        VoyagePastTrailModel!.addEventListener(VoyagePastTrailModelEvents.TrackUpdated, this._dataFetched);
        this._toggleTimeLine = this._toggleTimeLine.bind(this);
        if(localStorage.getItem("timeLineView")!==undefined) {
            this._timeLineVisible = localStorage.getItem("timeLineView")=="true";
        }
    }

    static styles = [
        voyageCursorStyles,
        css`
            :host {
                display: block;
            }

            h1 {
                padding: 0;
                margin: 0;
                font-weight: lighter;
                font-style: normal;
                font-family: MaerskHeadline;
                font-size: 26px;
                line-height: 32px;
                color: var(--neutrals-grey-02);
            }

            header {
                display: flex;
                flex-direction: row;
                column-gap: 4px;
                padding: 24px 16px;
            }

            header button {
                background: none;
                border: none;
                margin-top: 6px;
                padding: 0;
                display: flex;
                align-items: baseline;
                margin-left: -6px;
            }


            .content-container {
                padding: 0px 16px 24px 16px;
                border-bottom: 1px solid rgb(207, 207, 207);
                box-shadow: var(--voyage-box-shadow) 0px 6px 10px;
                color: var(--neutrals-grey-02);
            }

            .dropdown-container {
                display: flex;
                justify-content: space-between;
            }

            .dropdown-wrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                position: relative;
                margin-top: 3px;
                z-index: 100;
                width: 187px;
                margin-left: 8px;
            }

            voy-dropdown {
                height: 36px;
                max-width: 100%;
                font-size: 12px;
                z-index: 100;
                margin-top: 2px;
                color: var(--neutrals-grey-02);
            }

            .dropdown-title {
                display: flex;
                align-items: center;
            }

            /* filler to make space for dropdown. dropdown-container has to be position:fixed to overlap the maximized chart */
            .dropdown-space-filler {
                height: 50px;
            }

            .input-container {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-family: MaerskText;
                box-sizing: border-box;
                height: 36px;
                border: 1px solid var(--neutrals-grey-06);
                background-color: var(--neutrals-grey-10, white);
                border-radius: 3px;
                width: 187px;
                margin-left: 15px;
            }

            .input-container:focus-within {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
            }

            .input-container[invalid] {
                border: 1px solid var(--voyage-error-red, red);
            }

            label {
                font-size: 14px;
            }

            .input-container input {
                display: flex;
                font-family: MaerskText;
                font-size: 14px;
                box-sizing: border-box;
                padding-left: 10px;
                padding-right: 5px;
                border: none;
                outline: none;
                color: var(--neutrals-grey-02);
                background-color: unset;
                width: 100%;
            }
            
            .input-container input::-webkit-datetime-edit-year-field {
                color: var(--neutrals-grey-02);
            }

            .inputContainer input::-webkit-calendar-picker-indicator {
                background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23bbbbbb" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
            }

            .dates-container {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
            }

            .input-and-header {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                margin-top: 16px;
            }

            .graph {
                width: 100%;
                height: 200px;
            }

            .show-table-container {
                display: flex;
                justify-content: space-between;
                padding: 18px 10px 15px 0px;
                align-items: center;
            }

            .expand-container {
                display: flex;
                justify-content: center;
                align-items: center;
                position: absolute;
                margin-left: calc(100% - 15px);
                height: 25px;
                width: 25px;
                background-color: var(--performance-viewer-dial-text-color);
                z-index: 10;
                cursor: pointer;
                right: 16px;
                top: 16px;
            }

            .expand-container > svg {
                fill: var(--primary-blue-01, black);
                height: 100%;
                width: 100%;
            }

            .chart-table-container{
                display: none;
            }

            .dates-wrapper {
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
            }

            .chart-table-container[showContainer] {
                display: block;
                position: relative;
                padding-top: 20px;
                padding-left: 10px;
                max-height: calc(100vh - 465px);
                overflow-x: hidden;
            }
            .chart-table-container-full[showContainer] {
                max-height: calc(100vh - 408px);
            }

            .chart-table-container[maximized] {
                width: calc(100vw - 86px);
                background-color: var(--performance-viewer-dial-text-color);
                position: fixed;
                bottom: 56px;
                max-height: calc(100vh - 408px);
                overflow: hidden;
                box-shadow: var(--voyage-box-shadow) 6px 0px 10px;
            }
            .chart-table-container-full[maximized] {
                bottom:0px;
            }

            .export-csv-button {
                margin-right: 5px;
                border: 1px solid var(--neutrals-grey-06, grey);
                display: flex;
                border-radius: 3px;
                background: none;
                padding: 0px;
            }

            .export-csv-button span{
                padding: 6px 12px;
                font-family: MaerskText;
                font-size: 12px;
                color: var(--neutrals-grey-02);
            }
            
            .show-data-table-button {
                display: flex;
                align-items: center;
                border: none;
                background: none;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--voyage-blue-dark, blue);
            }

            .show-data-table-button svg {
                padding-left: 6px;
                width: 16px;
                height: 14px;
                rotate: 90;
                fill: var(--voyage-blue-dark, blue);
            }
            .weather-and-export-buttons {
                display: flex;
            }

            .toggle-weather {
                display: flex;
                padding: 0 24px;
                align-items: center;
            }
            .toggle-weather[hide] {
                display: none;
            }
            .hidden {
                display: none;
            }
            .weather-spinner-container{
                position: relative;
                width: 35px;
                align-self: flex-end;
                padding: 0 10px;
            }
            .weather-spinner-container .weather-loading-spinner voyage-spinner{
                width: 25px;
                height: 25px;
            }
            mc-button::part(button) {
                background-color: var(--primary-blue-900, blue);
                color: var(--neutrals-grey-10, white);
            }
            .premium-icon svg{
                height: 16px;
                width: 16px;
        }
        `
        ];

    render() {
        let className = this._timeLineVisible ? "chart-table-container" : "chart-table-container chart-table-container-full"
        const isPremium:boolean = this._vessel?.vesselVersion === VesselVoyageVersion.Premium;

        return html`
            <section>
                <header>
                    <button @click=${() => this._goToSubView(FiSubView.VesselDetails)} data-test="back-button">${SvgIcons.customChevronLeft}</button>
                    <h1><span class="premium-icon" ?hidden=${!isPremium}>${SvgIcons.customPremiumVessel}</span>${this._vessel?.vesselName || ""}<br>Past trail</h1>
                </header>
                <div class="content-container">
                    <div class="dates-wrapper">
                        <div>
                            <div class="dropdown-container">
                                <div class="dropdown-title"><label for="quick-date">Period</label></div>
                                <div class="dropdown-wrapper">
                                    <voy-dropdown .initialSelectedItem=${this._initialSelectedItem} .customDisplayText=${this._customDisplayText} .dropdownData=${dropdownData} @item-selected=${this._applyQuickDate}></voy-dropdown>
                                </div>
                            </div>
                            <div class="dates-wrapper">
                                <div class="dates-container">
                                    <div class="input-and-header">
                                        <div class="input-title"><label for=${DateType.fromDate}>From</label></div>
                                        <div class="input-container">
                                            <input id=${DateType.fromDate} type="datetime-local" min=${this._datepickerMinValue} max=${this._datepickerMaxValue} .value=${this._fromDate.substring(0,16)} @blur=${this._dateHandler}>
                                        </div>
                                    </div>
                                    <div class="input-and-header">
                                        <div class="input-title"><label for=${DateType.toDate}>To</label></div>
                                        <div class="input-container">
                                            <input id=${DateType.toDate} type="datetime-local" min=${this._datepickerMinValue} max=${this._datepickerMaxValue} .value=${this._toDate.substring(0,16)} @blur=${this._dateHandler}>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <mc-button label="Go" fit="small" @click=${this._updatePerformanceData}></mc-button>
                    </div>
                </div>
                <div class=${className} ?maximized=${this._maximized} ?showContainer=${this._showContainer}>
                    <div class="expand-container" title="${this._maximized ? 'Minimize performance charts' : 'Maximize performance charts'}" @click=${this._toggleMaximized}>
                        ${this._maximized ? SvgIcons.shrink : SvgIcons.expand}
                    </div>
                    ${this._renderChartContent()}
                    <div class="show-table-container">
                        <button class="show-data-table-button" @click=${this._toggleDataTable}>
                            <span>Show data table</span> ${this._dataTableExpanded ? html`${SvgIcons.lightChevronUp}` : html`${SvgIcons.lightChevronDown}`}
                        </button>
                        <div class="weather-and-export-buttons">
                            <div class="toggle-weather" ?hide=${!this._maximized || !this._dataTableExpanded}>
                                
                                <voyage-checkbox ?disabled=${!this._pastTrailData}
                                                .checked=${this._showWeather}
                                                @input=${(evt:InputEvent) => this._fetchWeather((<VoyageCheckbox>evt.target).checked)}
                                                .label=${"Show Weather"}
                                                >
                                </voyage-checkbox>
                                ${this._weatherDataIsFetching && this._showWeather ?
                                    html`<div class="weather-spinner-container">
                                            <div class="weather-loading-spinner">
                                                <voyage-spinner .transparent=${true}></voyage-spinner>
                                            </div>
                                </div>` : html`<div class="weather-spinner-container"></div>`}
                            </div>
                            <button @click=${this._exportCSV} class="export-csv-button" ?disabled=${!this._pastTrailData}><span>Export to CSV</span></button>
                        </div>
                    </div>
                    <div class="data-table-container" ?hidden=${!this._dataTableExpanded}>
                        <voy-past-trail-table .tableExpanded=${this._dataTableExpanded} .showWeather=${this._showWeather} .exportExcel=${false} .positions=${this._pastTrailDataReversed} .maximized=${this._maximized}></voy-past-trail-table>
                    </div>
                </div>
            </section>
        `;
    }

    firstUpdated() {
        this._setVessel();
        this._setDefaultFromDate();
        this._toDate = this._formatDateForDatepicker(new Date());
        this._updatePerformanceData();

    }



    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("_pastTrailData")) {
            this._updateChart();
            this._weatherDataIsFetching = false
        }

        if (changedProperties.has("_datepickerMaxValue")) {
            this._updateDatepickerMinValue()
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this._setVessel();
        this._updatePerformanceData();
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this._setDefaultFromDate(); // when exiting pastTrailView, reset trail back to default fromDate
        clearInterval(this._datepickerMaxValueInterval);
        VoyagePastTrailModel!.removeEventListener(VoyagePastTrailModelEvents.TrackUpdated, this._dataFetched); 
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.TimeLineViewToggle, this._toggleTimeLine);
    }

    private _setDefaultFromDate() {
        if( this._vessel?.operatorCode === OperatorId.NonMSK ) {
            this._getFromDateForNonMaerskVessel();
        } else {
            const fromDate = this._getFromDateFromSchedule();
            if (fromDate) {
                this._fromDate = this._formatDateForDatepicker(fromDate);
            }

            this._updateDatepickerMinValue();
        }
    }

    private _exportCSV() {
        if(!this._pastTrailData) return;

        let headers = tableTitles.join(';') + '\n';
        const keys = positionCols.concat(weatherCols);

        const rows = this._pastTrailData.map(pastTrailData => {
            return keys.reduce((prev, curr) => {

                let pastTrailEl = pastTrailData[curr];

                let value;
                if ((curr === 'latitude' || curr ===  'longitude') && pastTrailEl !== null) {
                     value = (<string>pastTrailEl).replace('° ', '-').replace('′', '');
                } else {
                    value = formatPastTrailValue(pastTrailEl, curr);
                }
                
                if (prev.length === 0) {
                    return `${value || ''}`;
                }
                return `${prev};${value || ''}`;
            }, '')

        }).join('\n');

        const csvContent = 'sep=;\n' + headers + rows;

        const csvData = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });  
        const csvUrl = URL.createObjectURL(csvData);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.download = 'PastTrailData.csv';
        hiddenElement.click();
    }

    private _updateDatepickerMinValue() {
        const newMinValue = new Date();
        newMinValue.setMonth( newMinValue!.getMonth() - 1 );
        this._datepickerMinValue = this._formatDateForDatepicker(newMinValue);
    }

    private _toggleMaximized() {
        this._maximized = !this._maximized;
    }

    private _toggleDataTable() {
        this._dataTableExpanded = !this._dataTableExpanded;
    }

    private _getFromDateForNonMaerskVessel() {
        let fromDate = new Date();
        fromDate.setDate(fromDate!.getDate() - 3);
        this._fromDate = this._formatDateForDatepicker(fromDate);
        this._customDisplayText = QuickDates.Day_3;
    }

    private _getFromDateFromSchedule(): undefined | Date {
        if (!this.imo) return;

        const scheduleInfo = VoyageSchedulesModel?.currentScheduleStates?.find(s => s.imoNo === this.imo)
        // The timestamp is returned with full timezone information (e.g. 2019-07-01T12:00:00.000+01:00), so trim off everything except the date, hours and minutes

        if (!scheduleInfo) return;

        const timeOffset = (new Date().getTimezoneOffset()) * 60 * 1000; 

        return new Date(scheduleInfo.schedule.startTime - timeOffset)
    }
    
    public async _setVessel() {
        if (!this.imo) return;
        this._vessel = VoyageShoreContextModel!.vessels.find(v => v.imoNo === this.imo);
        if(!this._vessel){
            await VesselsModel?.refreshVessels()
            this._vessel = VesselsModel!.getVessel(this.imo)
        } 
    }

    private _goToSubView(view: FiSubView) {
        UiStateModel!.setValue(UiStateModelParam.FISubView, view);
    }

    private _applyQuickDate(e: CustomEvent) {
        this._selectedQuickDate = e.detail.selectedItem;
        let newFromDate = new Date();

        switch(this._selectedQuickDate) {
            case QuickDates.CurrentVoyage:
                newFromDate = this._getFromDateFromSchedule()!;
                break;
            case QuickDates.Hours_12:
                newFromDate.setHours( newFromDate!.getHours() - 12 );
                break;
            case QuickDates.Day_1:
                newFromDate.setDate( newFromDate!.getDate() - 1 );
                break;
            case QuickDates.Day_3:
                newFromDate.setDate( newFromDate!.getDate() - 3 );
                break;
            case QuickDates.Month_1:
                newFromDate.setMonth( newFromDate!.getMonth() - 1 );
                break;
        }

        this._initialSelectedItem = null;
        this._customDisplayText = null;
        this._fromDate = this._formatDateForDatepicker(newFromDate);
        this._toDate = this._formatDateForDatepicker(new Date());
        this._showWeather = false;
        this._dataIsFetching = false;
    }

    private _formatDateForDatepicker(date: Date) {
        return date.toISOString();
    }

    private _dateHandler(e: Event) {
        const now = new Date(this._datepickerMaxValue);
        const selectedDate = (<HTMLInputElement>e.target).value;
        const inputId = (<HTMLInputElement>e.target).id;
        if (!selectedDate) return;

        const parsedSelectedDate = new Date(selectedDate); // need to parse string to Date to compare Dates

        if (inputId === DateType.fromDate && parsedSelectedDate < new Date(this._toDate)) {
            this._fromDate = selectedDate;
        } else if (inputId === DateType.toDate && parsedSelectedDate < now) {
            this._toDate = selectedDate;
        }
        this._customDisplayText = "Custom";
    }

    // documentation: https://echarts.apache.org/examples/en/editor.html?c=line-sections
    private _extractChartData(pastTrailData: PastTrailData[]) {

        const speedValues: any[] = [];
        const speedTimestamps = [];
        
        // all No Signal segments
        const allSegmentsValues: number[][] = [];
        const allSegmentsTimestamps: number[][] = [];

        // variables for the segment that is currently being created
        let previousItemWasNull = false;
        let currentSegmentTimestamps: number[] = [];
        let currentSegmentValues: number[] = [];
        let currentSegmentStartValue: number = 0;
        let currentSegmentEndValue;

        const sortedPastTrailData = pastTrailData.sort((a,b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        for (const [index, dataEntry] of sortedPastTrailData.entries()) {
            const timestamp = new Date(dataEntry.timestamp).getTime();
            speedValues.push(dataEntry.speed);
            speedTimestamps.push(timestamp);

            const isLastDataEntry = index === sortedPastTrailData.length - 1;
            const startNewSegment = (index === 0 || !previousItemWasNull) && dataEntry.speed === null;
            const continueCurrentSegment = previousItemWasNull && dataEntry.speed === null;
            const endOfSegmentReached = previousItemWasNull && (dataEntry.speed !== null || isLastDataEntry);

            if (startNewSegment) {
                currentSegmentTimestamps.push(timestamp); // first date
                currentSegmentStartValue = speedValues[speedValues.length-2]; // first value
                previousItemWasNull = true;
            }
            
            if (continueCurrentSegment) {
                currentSegmentTimestamps.push(timestamp);
            }

            if (endOfSegmentReached) {
                currentSegmentTimestamps.push(timestamp);

                if (isLastDataEntry && dataEntry.speed === null) {
                    currentSegmentEndValue = currentSegmentStartValue;
                    // if last speed value of whole dataset is null, we don't know the 'end' speed of current segment
                    // show a linear line from previous speed value
                    currentSegmentValues = Array.from({length: currentSegmentTimestamps.length}, () => currentSegmentStartValue);
                }
                else if (dataEntry.speed) {
                    currentSegmentEndValue = dataEntry.speed;
                    // create array of values which makes up a line between start and end of current segment
                    let diff = currentSegmentEndValue! - currentSegmentStartValue;
                    const incrementValue = diff / currentSegmentTimestamps.length;
                    let accumulatedValue = currentSegmentStartValue;

                    for (let i = 0; i < currentSegmentTimestamps.length; i++) {
                        accumulatedValue = accumulatedValue + incrementValue;
                        currentSegmentValues.push(accumulatedValue);
                    }
                }

                // finished creating current segment - add it to collection
                allSegmentsValues.push(currentSegmentValues)
                allSegmentsTimestamps.push(currentSegmentTimestamps)

                // reset current segment
                currentSegmentValues = [];
                currentSegmentTimestamps = [];
                previousItemWasNull = false;
            }
        }
      
        if( this._selectedQuickDate !== QuickDates.CurrentVoyage ) {
            if( speedTimestamps[0] !== new Date(this._fromDate).getTime() ) {
                const firstTimestamp = new Date(this._fromDate).getTime()
                speedValues.unshift(null);
                speedTimestamps.unshift(firstTimestamp);
            }

            if( speedTimestamps[speedTimestamps.length - 1] !== new Date(this._toDate).getTime() ) {
                const lastTimestamp = new Date(this._toDate).getTime()
                speedValues.push(null);
                speedTimestamps.push(lastTimestamp);
            }
        }
        
        return {
            speedValues,
            speedTimestamps,
            allSegmentsValues,
            allSegmentsTimestamps
        };
    }

    private _updateChart() {
        if (this._pastTrailData && this._pastTrailData.length > 0) {
            const chartData = this._extractChartData(this._pastTrailData);

            if (chartData) {
                this._chartData = [{
                    name: "actual",
                    color: "--performance-viewer-actual-color",
                    timeseries: {
                        values: chartData.speedValues,
                        timestamps: chartData.speedTimestamps,
                    },
                    fillArea: true,
                    animate: true,
                }]
            }
            
            this._chartStyling = {
                nameRotate: 0,
                nameLocation: 'end',
                padding: [0, 10, 0, 0],
                nameGap: 9,
            }

            for (let i = 0; i < chartData.allSegmentsValues.length; i++) {
                const missingPositionData = {
                    name: "No signal",
                    color: "--performance-viewer-actual-no-signal-color",
                    timeseries: {
                        values: chartData.allSegmentsValues[i],
                        timestamps: chartData.allSegmentsTimestamps[i],
                    },
                    fillArea: true,
                    animate: true,
                    lineDash: true
                }

                this._chartData?.push(missingPositionData)
            }
        } else {
            this._chartData = [];
        }
    }

    private _updatePerformanceData() {
        if (!this.imo) {
            return;
        }

        this._pastTrailData = null;

        if (this._fromDate && this._toDate && !this._dataIsFetching) {
            // show spinner after 1.5 seconds if user is still waiting for PAPI data
            this._spinnerTimeout = setTimeout(() => {
                this._showLoadingSpinner = true;
            }, 1500);
            this._dataIsFetching = true;
            this._showContainer = true;
            const formatDates = (date:Date) => {
                return Date.UTC(date.getFullYear(),date.getMonth(),date.getDate(),date.getHours(),date.getMinutes(),date.getSeconds())
            }
            
            VoyagePastTrailModel!.createPastTrailSegment(this.imo!, formatDates(new Date(this._fromDate)), formatDates(new Date(this._toDate)));
        }
    }


    private _fetchWeather(checked:boolean) {
        this._showWeather = checked;
       
        const hasFetched =  VoyagePastTrailModel!.hasWeather()
        if (checked && !hasFetched) {
            this._weatherDataIsFetching = true;
            VoyagePastTrailModel!.fetchWeather()
        }
    }

    private _dataFetched() {
        this._pastTrailData = VoyagePastTrailModel!.trackSegment;
        this._pastTrailDataReversed = Array.from(this._pastTrailData).reverse();

        this._dataIsFetching = false;
        clearTimeout(this._spinnerTimeout);
        this._showLoadingSpinner = false;
    }

    private _updateNowValue() {
        this._datepickerMaxValue = this._formatDateForDatepicker(new Date());
    }

    private _renderChartContent(): TemplateResult {
        if (!this._dataIsFetching && this._chartData.length === 0) {
            return html `<p class="no-chart">
                Sorry - we can not show you a graph, as there is no performance data available for this vessel
            </p>`
        }

        return html `
            <div class="graph"> 
                <!-- add these properties to the voy-timeseries-chart when alarms is merged -->
                <voy-timeseries-chart
                    .data=${this._chartData}
                    unit="Sog (Kt)" id="other"
                    .showLegend=${true}
                    .leftPadding=${30}
                    .yAxisChartParams=${this._chartStyling}
                    .displayNoDataMessage=${false}
                    .showSpinner=${this._showLoadingSpinner}>
                </voy-timeseries-chart>
            </div>
        `
    }

    private _toggleTimeLine():void {
        this._timeLineVisible = !this._timeLineVisible
    }
}
